import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Field, SummaryBar, DataTable } from "@/pages/FridgeLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { Plus, Trash2, ExternalLink, Pencil, Upload, Download } from "lucide-react";
import { toast } from "sonner";

const COURSE_GROUPS = {
  "Food Safety & Hygiene": [
    "Level 1 Food Safety & Hygiene",
    "Level 2 Food Safety & Hygiene",
    "Level 3 Food Safety & Hygiene",
    "Level 4 Food Safety & Hygiene",
    "Foundations in Food Safety",
    "The Complete Food Safety Certificate",
    "Food Safety Cross Contamination",
  ],
  "Supervising Food Safety": [
    "Level 1 Supervising Food Safety",
    "Level 2 Supervising Food Safety",
    "Level 3 Supervising Food Safety",
  ],
  HACCP: ["HACCP Level 1", "HACCP Level 2", "HACCP Level 3"],
  "Allergens & Labelling": [
    "Food Allergen Awareness",
    "Natasha's Law Training",
    "Food Labelling Regulations (UK)",
    "Food Standards Agency Food Safety",
  ],
  "Occupational Health & Safety": [
    "IOSH Managing Safely",
    "NEBOSH General Certificate in Occupational Health and Safety",
  ],
};

const COURSE_RENEWAL_YEARS = {
  "Level 1 Food Safety & Hygiene": 3,
  "Level 2 Food Safety & Hygiene": 3,
  "Level 3 Food Safety & Hygiene": 3,
  "Level 4 Food Safety & Hygiene": 3,
  "Foundations in Food Safety": 3,
  "The Complete Food Safety Certificate": 3,
  "Food Safety Cross Contamination": 3,
  "Level 1 Supervising Food Safety": 3,
  "Level 2 Supervising Food Safety": 3,
  "Level 3 Supervising Food Safety": 3,
  "HACCP Level 1": 3,
  "HACCP Level 2": 3,
  "HACCP Level 3": 3,
  "Food Allergen Awareness": 3,
  "Natasha's Law Training": 3,
  "Food Labelling Regulations (UK)": 3,
  "Food Standards Agency Food Safety": 3,
  "IOSH Managing Safely": null,
  "NEBOSH General Certificate in Occupational Health and Safety": null,
};

// Sentinel used in the course-picker Select to reveal a free-type input for
// courses not in the standard list.
const CUSTOM_COURSE = "__custom__";

function addYears(dateStr, years) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

const COURSE_TO_GROUP = Object.entries(COURSE_GROUPS).reduce((acc, [g, list]) => {
  list.forEach((c) => (acc[c] = g));
  return acc;
}, {});

const ALL_GROUPS = ["All", ...Object.keys(COURSE_GROUPS)];

const empty = {
  staff_name: "",
  staff_email: "",
  course_name: "",
  completion_date: new Date().toISOString().slice(0, 10),
  expiry_date: "",
  expiry_na: false,
  certificate_url: "",
  notes: "",
};

function certStatus(expiry) {
  if (!expiry) return "no_expiry";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ex = new Date(expiry); ex.setHours(0, 0, 0, 0);
  const days = Math.round((ex - today) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "renew";
  return "active";
}

function pillToneFor(status) {
  if (status === "expired") return "alert";
  if (status === "renew") return "warning";
  if (status === "active") return "success";
  if (status === "no_expiry") return "brand";
  return "neutral";
}

function pillLabelFor(status) {
  return status === "no_expiry" ? "no expiry" : status;
}

const CSV_TEMPLATE =
  "staff_name,staff_email,course_name,completion_date,expiry_date\n" +
  "Jane Smith,jane@hearth.co,Level 2 Food Safety & Hygiene,2025-02-01,2028-02-01\n" +
  "Marco Rossi,marco@hearth.co,HACCP Level 2,2024-09-15,\n";

function parseCsv(text) {
  // Simple CSV: split lines, handle optional quoted fields.
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return [];
  const header = splitRow(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = splitRow(line);
    const row = {};
    header.forEach((h, i) => (row[h] = (cells[i] ?? "").trim()));
    return row;
  });
}

function splitRow(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

export default function Training() {
  const [items, setItems] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [isCustom, setIsCustom] = useState(false); // free-type course mode
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterGroup, setFilterGroup] = useState("All");
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const fileRef = useRef(null);

  const load = async () => setItems((await api.get("/training")).data);
  const loadStaff = async () => {
    try { setStaffList((await api.get("/staff")).data); } catch { setStaffList([]); }
  };
  useEffect(() => { load(); loadStaff(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setIsCustom(false);
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setForm({
      staff_name: record.staff_name || "",
      staff_email: record.staff_email || "",
      course_name: record.course_name || "",
      completion_date: record.completion_date || empty.completion_date,
      expiry_date: record.expiry_date || "",
      expiry_na: !record.expiry_date,
      certificate_url: record.certificate_url || "",
      notes: record.notes || "",
    });
    // Legacy or previously free-typed course: reveal the custom input on open.
    setIsCustom(!!record.course_name && !COURSE_TO_GROUP[record.course_name]);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.course_name || !form.course_name.trim()) {
      toast.error(isCustom ? "Please type the course name" : "Please select a course");
      return;
    }
    setBusy(true);
    try {
      const { expiry_na, ...payload } = form;
      const body = {
        ...payload,
        expiry_date: expiry_na ? null : (payload.expiry_date || null),
      };
      if (editingId) {
        await api.put(`/training/${editingId}`, body);
        toast.success("Record updated");
      } else {
        await api.post("/training", body);
        toast.success("Training recorded");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/training/${id}`);
    toast.success("Record removed");
    load();
  };

  // ---- CSV import
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    e.target.value = "";
    const rows = parseCsv(text);
    if (rows.length === 0) {
      toast.error("CSV is empty");
      return;
    }
    const errors = [];
    const valid = [];
    rows.forEach((r, idx) => {
      const lineNo = idx + 2;
      if (!r.staff_name) errors.push(`Row ${lineNo}: missing staff_name`);
      if (!r.course_name) errors.push(`Row ${lineNo}: missing course_name`);
      if (!r.completion_date) errors.push(`Row ${lineNo}: missing completion_date`);
      // Custom / non-standard course names are permitted — the "Custom" free-type
      // option means we shouldn't reject unknown names here. Just persist the label.
      if (r.staff_name && r.course_name && r.completion_date) {
        valid.push({
          staff_name: r.staff_name,
          staff_email: r.staff_email || "",
          course_name: r.course_name,
          completion_date: r.completion_date,
          expiry_date: r.expiry_date ? r.expiry_date : null,
          certificate_url: r.certificate_url || "",
          notes: r.notes || "",
        });
      }
    });
    setImportPreview(valid);
    setImportErrors(errors);
    setImportOpen(true);
  };

  const confirmImport = async () => {
    if (importPreview.length === 0) {
      toast.error("Nothing to import");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/training/bulk", importPreview);
      toast.success(`Imported ${data.created} record${data.created === 1 ? "" : "s"}`);
      setImportOpen(false);
      setImportPreview([]);
      setImportErrors([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "training-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- derived
  const filtered = useMemo(() => {
    if (filterGroup === "All") return items;
    return items.filter((i) => COURSE_TO_GROUP[i.course_name] === filterGroup);
  }, [items, filterGroup]);

  const expired = filtered.filter((b) => certStatus(b.expiry_date) === "expired").length;
  const renew = filtered.filter((b) => certStatus(b.expiry_date) === "renew").length;
  const staff = new Set(filtered.map((i) => i.staff_name)).size;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="training-page">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={onFileChosen}
        data-testid="training-csv-input"
      />

      <PageHeader
        eyebrow="People & certification"
        title="Staff training"
        description="Keep your team's food-safety, hygiene, and allergen training up to date."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="training-import-button"
              onClick={() => fileRef.current?.click()}
              className="bg-white hover:bg-[#efece6] text-[#1c1917] border border-[#e5e2d9] rounded-[4px] h-10 px-4"
            >
              <Upload className="w-4 h-4 mr-2" /> Import CSV
            </Button>
            <Button
              data-testid="training-add-button"
              onClick={openCreate}
              className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5"
            >
              <Plus className="w-4 h-4 mr-2" /> Log training
            </Button>
          </div>
        }
      />

      {/* Category filter */}
      <div className="flex flex-wrap items-center gap-1 mb-6 border-b border-[#e5e2d9] pb-1" data-testid="training-filter-bar">
        {ALL_GROUPS.map((g) => (
          <button
            key={g}
            data-testid={`training-filter-${g.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            onClick={() => setFilterGroup(g)}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors border-b-2 -mb-[1px] ${
              filterGroup === g
                ? "border-[#1b3b36] text-[#1b3b36]"
                : "border-transparent text-[#8a8476] hover:text-[#1c1917]"
            }`}
          >
            {g}
            <span className="ml-2 font-mono text-[10px] text-[#8a8476]">
              {g === "All"
                ? items.length
                : items.filter((i) => COURSE_TO_GROUP[i.course_name] === g).length}
            </span>
          </button>
        ))}
      </div>

      <SummaryBar items={[
        { label: "Trained staff", value: staff, testid: "stat-staff" },
        { label: "Certs renew ≤ 30d", value: renew, tone: renew ? "warning" : "success", testid: "stat-renew" },
        { label: "Expired certs", value: expired, tone: expired ? "alert" : "success", testid: "stat-cert-expired" },
      ]} />

      <DataTable
        empty={filterGroup === "All"
          ? "No training records yet. Log your first course completion or import a CSV."
          : `No records in ${filterGroup}.`}
        head={["Staff", "Course", "Completed", "Expires", "Status", "Cert", ""]}
        rows={filtered}
        testid="training-table"
        render={(t, idx) => {
          const status = certStatus(t.expiry_date);
          return (
            <tr key={t.id} data-testid={`training-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40">
              <td className="px-4 py-3 text-sm">
                <div className="font-medium">{t.staff_name}</div>
                <div className="text-xs text-[#8a8476]">{t.staff_email || "—"}</div>
              </td>
              <td className="px-4 py-3 text-sm">
                <div>{t.course_name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476] mt-0.5">
                  {COURSE_TO_GROUP[t.course_name] || "Other"}
                </div>
              </td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">{t.completion_date}</td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">{t.expiry_date || "N/A"}</td>
              <td className="px-4 py-3">
                <StatusPill tone={pillToneFor(status)}>{pillLabelFor(status)}</StatusPill>
              </td>
              <td className="px-4 py-3 text-sm">
                {t.certificate_url ? (
                  <a href={t.certificate_url} target="_blank" rel="noopener noreferrer" data-testid={`training-cert-link-${idx}`} className="text-[#1b3b36] hover:underline inline-flex items-center gap-1">
                    view <ExternalLink className="w-3 h-3" />
                  </a>
                ) : "—"}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-3">
                  <button data-testid={`training-edit-${idx}`} onClick={() => openEdit(t)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`training-delete-${idx}`} onClick={() => del(t.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          );
        }}
      />

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Edit training record" : "New training record"}
            </DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submit} data-testid="training-form">
            <Field label="Staff member">
              {staffList.length > 0 ? (
                <Select
                  value={form.staff_name}
                  onValueChange={(v) => {
                    if (v === "__manual__") {
                      setForm((f) => ({ ...f, staff_name: "", staff_email: "" }));
                      return;
                    }
                    const staff = staffList.find((s) => s.name === v);
                    setForm((f) => ({
                      ...f,
                      staff_name: v,
                      staff_email: staff?.email || "",
                    }));
                  }}
                >
                  <SelectTrigger data-testid="training-staff-select" className="bg-white rounded-[4px]">
                    <SelectValue placeholder="Select a staff member" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {staffList.map((s) => (
                      <SelectItem key={s.id} value={s.name} data-testid={`training-staff-option-${s.id}`}>
                        <span className="font-medium">{s.name}</span>
                        {s.email && <span className="text-[#8a8476] ml-2">{s.email}</span>}
                      </SelectItem>
                    ))}
                    <SelectItem value="__manual__" data-testid="training-staff-option-manual">
                      <span className="text-[#8a8476] italic">+ Type a name manually</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-xs text-[#8a8476] bg-[#fdf3e1] border border-[#d9953d]/30 px-3 py-2 rounded-[3px]">
                  No staff records yet. Add team members on the Staff records page to enable the dropdown.
                </div>
              )}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Input
                  required
                  data-testid="training-staff-input"
                  value={form.staff_name}
                  onChange={(e) => setForm((f) => ({ ...f, staff_name: e.target.value }))}
                  className="bg-white rounded-[4px]"
                  placeholder="Staff name"
                />
                <Input
                  type="email"
                  data-testid="training-email-input"
                  value={form.staff_email}
                  onChange={(e) => setForm((f) => ({ ...f, staff_email: e.target.value }))}
                  className="bg-white rounded-[4px]"
                  placeholder="Staff email"
                />
              </div>
            </Field>
            <Field label="Course">
              <Select
                value={isCustom ? CUSTOM_COURSE : form.course_name}
                onValueChange={(v) => {
                  if (v === CUSTOM_COURSE) {
                    setIsCustom(true);
                    setForm((f) => ({ ...f, course_name: "", expiry_na: false }));
                    return;
                  }
                  setIsCustom(false);
                  setForm((f) => {
                    const next = { ...f, course_name: v };
                    // Auto-suggest expiry based on course (only when not editing
                    // an existing record with a custom date already)
                    const years = COURSE_RENEWAL_YEARS[v];
                    if (years === null) {
                      next.expiry_na = true;
                      next.expiry_date = "";
                    } else if (typeof years === "number") {
                      next.expiry_na = false;
                      next.expiry_date = addYears(f.completion_date, years);
                    }
                    return next;
                  });
                }}
              >
                <SelectTrigger data-testid="training-course-select" className="bg-white rounded-[4px]">
                  <SelectValue placeholder="Select a course" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {Object.entries(COURSE_GROUPS).map(([group, courses]) => (
                    <SelectGroup key={group}>
                      <SelectLabel className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">{group}</SelectLabel>
                      {courses.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">Other</SelectLabel>
                    <SelectItem value={CUSTOM_COURSE} data-testid="training-course-custom-option">
                      + Custom course (type your own)
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              {isCustom && (
                <div className="mt-2">
                  <Input
                    data-testid="training-course-custom-input"
                    value={form.course_name}
                    onChange={(e) => setForm((f) => ({ ...f, course_name: e.target.value }))}
                    className="bg-white rounded-[4px]"
                    placeholder="Type the course or certificate name"
                    autoFocus
                    maxLength={140}
                  />
                  <div className="text-[10px] text-[#8a8476] mt-1">Custom courses don't have an auto-calculated expiry — set it manually below, or tick "No expiry".</div>
                </div>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Completed">
                <Input type="date" required data-testid="training-complete-input" value={form.completion_date} onChange={(e) => setForm((f) => {
                  const next = { ...f, completion_date: e.target.value };
                  const years = COURSE_RENEWAL_YEARS[f.course_name];
                  if (typeof years === "number" && !f.expiry_na) {
                    next.expiry_date = addYears(e.target.value, years);
                  }
                  return next;
                })} className="bg-white rounded-[4px]" />
              </Field>
              <Field label="Expires">
                <div className="space-y-2">
                  <Input
                    type="date"
                    data-testid="training-expiry-input"
                    value={form.expiry_na ? "" : form.expiry_date}
                    disabled={form.expiry_na}
                    onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
                    className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:text-[#8a8476]"
                  />
                  <label className="flex items-center gap-2 text-xs text-[#57534e] cursor-pointer select-none">
                    <Checkbox
                      data-testid="training-expiry-na"
                      checked={form.expiry_na}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, expiry_na: !!v, expiry_date: v ? "" : f.expiry_date }))}
                      className="border-[#1b3b36] data-[state=checked]:bg-[#1b3b36]"
                    />
                    Not applicable — this course doesn&apos;t expire
                  </label>
                </div>
              </Field>
            </div>
            <Field label="Certificate URL">
              <Input data-testid="training-cert-input" value={form.certificate_url} onChange={(e) => setForm((f) => ({ ...f, certificate_url: e.target.value }))} className="bg-white rounded-[4px]" placeholder="https://..." />
            </Field>
            <Field label="Notes">
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-white rounded-[4px]" />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="training-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Saving…" : editingId ? "Save changes" : "Save record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import preview dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="training-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Review CSV import</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="text-[#1b3b36] font-medium">{importPreview.length}</span>
                <span className="text-[#8a8476]"> valid rows ready to import</span>
                {importErrors.length > 0 && (
                  <span className="text-[#d94f3d] ml-3">• {importErrors.length} error{importErrors.length === 1 ? "" : "s"}</span>
                )}
              </div>
              <button onClick={downloadTemplate} data-testid="training-template-download" className="text-xs text-[#1b3b36] inline-flex items-center gap-1 hover:underline">
                <Download className="w-3 h-3" /> Download template
              </button>
            </div>

            {importErrors.length > 0 && (
              <div className="bg-[#fbeae8] border border-[#d94f3d]/30 rounded-[4px] p-3 max-h-32 overflow-y-auto" data-testid="training-import-errors">
                {importErrors.map((e, i) => (
                  <div key={i} className="text-xs text-[#d94f3d] font-mono">{e}</div>
                ))}
              </div>
            )}

            {importPreview.length > 0 && (
              <div className="border border-[#e5e2d9] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#efece6]">
                      {["Staff", "Course", "Completed", "Expires"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.22em] text-[#8a8476] font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((r, i) => (
                      <tr key={i} className="border-t border-[#e5e2d9]" data-testid={`training-import-preview-${i}`}>
                        <td className="px-3 py-2">{r.staff_name}</td>
                        <td className="px-3 py-2">{r.course_name}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.completion_date}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.expiry_date || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                onClick={() => { setImportOpen(false); setImportPreview([]); setImportErrors([]); }}
                className="bg-[#efece6] hover:bg-[#e5e2d9] text-[#1c1917] rounded-[4px]"
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={busy || importPreview.length === 0}
                onClick={confirmImport}
                data-testid="training-import-confirm"
                className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]"
              >
                {busy ? "Importing…" : `Import ${importPreview.length} record${importPreview.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
